"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { getSavedAccountDisplayName } from "@/lib/auth/saved-accounts";
import type { SavedLoginAccount } from "@/lib/auth/saved-accounts";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

const twoFactorSchema = z.object({
	twoFactorToken: z
		.string()
		.length(6, "Token must be 6 digits")
		.regex(/^\d+$/, "Token must contain only numbers"),
});

type TwoFactorSchema = z.infer<typeof twoFactorSchema>;

type Props = {
	open: boolean;
	account: SavedLoginAccount | null;
	isSubmitting?: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (token: string) => Promise<void>;
};

export default function AccountSwitcherTwoFactorDialog({
	open,
	account,
	isSubmitting = false,
	onOpenChange,
	onSubmit,
}: Props) {
	const [error, setError] = useState<string | null>(null);
	const form = useForm<TwoFactorSchema>({
		resolver: zodResolver(twoFactorSchema),
		defaultValues: { twoFactorToken: "" },
	});

	useEffect(() => {
		if (!open) {
			form.reset();
			setError(null);
		}
	}, [form, open]);

	const accountName = account ? getSavedAccountDisplayName(account) : "this account";

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle>Two-factor authentication</DialogTitle>
					<DialogDescription>
						Enter the 6-digit code from your authenticator app to switch to {accountName}.
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form
						className="space-y-4"
						onSubmit={form.handleSubmit(async (values) => {
							setError(null);
							try {
								await onSubmit(values.twoFactorToken);
							} catch (submitError) {
								setError(
									submitError instanceof Error
										? submitError.message
										: "Invalid authentication code. Please try again.",
								);
							}
						})}
					>
						<FormField
							control={form.control}
							name="twoFactorToken"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Authentication code</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="000000"
											maxLength={6}
											className="text-center font-mono text-2xl tracking-widest"
											autoComplete="one-time-code"
											inputMode="numeric"
											autoFocus
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{error ? <p className="text-sm text-destructive">{error}</p> : null}
						<Button type="submit" variant="accent" className="w-full" disabled={isSubmitting}>
							{isSubmitting ? "Verifying..." : "Verify & switch"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
